import { useCallback, useEffect, useMemo, useRef } from 'react';

/**
 * Apply ±`ratio` jitter to a delay so pollers started at the same moment —
 * several tabs, or two polls inside one panel — don't fire in lockstep.
 *
 * Exported because one caller (the admin dashboard) schedules its own per-row
 * timers imperatively and so cannot use the hook, but should still decohere the
 * same way.
 */
export const jittered = (ms: number, ratio = 0.1): number => {
  const delta = ms * ratio;
  return Math.round(ms + (Math.random() * 2 - 1) * delta);
};

export interface UsePollingOptions<T> {
  /**
   * Fetch one sample and apply it to state.
   *
   * Whatever it returns is handed straight to `interval` to pick the next
   * delay. That is the whole reason it has a return value: reading React state
   * instead would read it *before* the `setState` from this very fetch had
   * flushed, so the first tick after a transition would pick the wrong cadence.
   */
  fetch: () => Promise<T | null>;
  /** Delay before the next tick, decided from the value `fetch` just returned. */
  interval: (latest: T | null) => number;
  /** Polling stops entirely while false — no project, panel closed, nothing in flight. */
  enabled?: boolean;
  jitterRatio?: number;
}

export interface UsePollingControls {
  /** Fetch immediately and restart the schedule. For use right after a user action. */
  refreshNow: () => void;
}

/**
 * Adaptive polling loop: jittered, self-pacing, and paused while the tab is hidden.
 *
 * Owns *when* to fetch. What to fetch, what to do with the result, and how fast
 * to go are the caller's — passed in as `fetch` and `interval`. Those differ per
 * screen and are not duplication.
 *
 * A `setTimeout` chain rather than `setInterval`, so every tick re-rolls its
 * jitter; concurrent pollers then decohere over time instead of staying merely
 * phase-shifted.
 */
export function usePolling<T>({
  fetch,
  interval,
  enabled = true,
  jitterRatio = 0.1,
}: UsePollingOptions<T>): UsePollingControls {
  // Held in refs rather than effect dependencies. A caller that re-renders with
  // a new closure would otherwise tear down and restart the timer chain on
  // every render — which re-rolls the jitter each time and, under frequent
  // re-renders, can starve the poll entirely because no timer survives to fire.
  const fetchRef = useRef(fetch);
  const intervalRef = useRef(interval);
  fetchRef.current = fetch;
  intervalRef.current = interval;

  const refreshRef = useRef<() => void>(() => {});
  const refreshNow = useCallback(() => refreshRef.current(), []);

  useEffect(() => {
    if (!enabled) {
      // Must not keep pointing at a live closure from a previous enabled run.
      refreshRef.current = () => {};
      return;
    }

    // Scoped to this effect run, not to the component. A fetch already in
    // flight when `enabled` flips false resolves *after* cleanup and after the
    // next effect run has begun — a component-level `mounted` ref would have
    // been reset to true by then, letting that stale fetch schedule a timer
    // nothing owns and quietly resume a loop that was supposed to stop.
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const clear = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const schedule = (latest: T | null) => {
      clear();
      // Nothing is scheduled while the tab is hidden; the visibilitychange
      // handler restarts the loop with a fresh fetch when the user returns.
      if (typeof document !== 'undefined' && document.hidden) return;
      timer = setTimeout(tick, jittered(intervalRef.current(latest), jitterRatio));
    };

    const tick = async () => {
      timer = null;
      const latest = await fetchRef.current();
      if (cancelled) return;
      schedule(latest);
    };

    const run = async () => {
      const latest = await fetchRef.current();
      if (!cancelled) schedule(latest);
    };

    refreshRef.current = () => {
      if (cancelled) return;
      clear();
      void run();
    };

    const handleVisibility = () => {
      if (typeof document === 'undefined' || cancelled) return;
      if (document.hidden) clear();
      else void run();
    };

    void run();

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibility);
    }

    return () => {
      cancelled = true;
      clear();
      refreshRef.current = () => {};
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibility);
      }
    };
  }, [enabled, jitterRatio]);

  return useMemo(() => ({ refreshNow }), [refreshNow]);
}

export default usePolling;
